/* build: 2026-06-22a — Nono own booking flow (replaces the Momence iframe).
 *
 * nono-own-booking.js — self-contained, vanilla-JS recreation of the Claude
 * design (Booking.dc.html): hero -> time -> details -> confirm -> intake -> done.
 *
 * Single source of truth for AVAILABILITY is OUR backend, NOT Momence:
 *   GET  {endpoint}/api/booking/availability   -> open days + slots (remaining)
 *   POST {endpoint}/api/booking                 -> create booking (capacity-guarded)
 *   POST {endpoint}/api/booking/intake          -> attach optional intake by ref
 *
 * The backend fires an accurate, person-level Meta CAPI `Schedule` on booking
 * (hashed PII + fbp/fbc + event_id=ref). We ALSO fire a client-side Pixel
 * `Schedule` with the same eventID so browser+server de-dupe.
 *
 * Editable copy: every user-facing string lives in DEFAULTS below and can be
 * overridden per-section from the theme editor via a `data-nob-config` JSON blob
 * (built in lp-own-booking.liquid). Missing keys fall back to DEFAULTS, so the
 * widget always renders fully even with an empty/!malformed blob.
 *
 * Honesty (per design ARCHITECTURE.md): no fabricated live "ser på nå" counter,
 * no medical claims; availability + "Opptatt" are real from the API.
 */
(function () {
  'use strict';

  var MAROON = '#580B26';

  // Inline SVG icons (stroke=currentColor so they inherit the element's color),
  // matching the Claude design's iconography instead of text glyphs.
  var ICO = {
    check: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12.5 L9.5 18 L20 6.5"/></svg>',
    checkBig: '<svg viewBox="0 0 24 24" width="42" height="42" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12.5 L9.5 18 L20 6.5"/></svg>',
    back: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>',
    arrow: '<svg class="nob-cta-arrow" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12h15M13 6l6 6-6 6"/></svg>',
    phone: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 5c0 8 7 15 15 15l2.5-2.5-4.5-3-2.5 2a12 12 0 0 1-4.5-4.5l2-2.5-3-4.5z"/></svg>',
    mail: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2.5"/><path d="M4 7 L12 13 L20 7"/></svg>',
  };

  var COUNTRIES = [
    { code: 'NO', name: 'Norge', dial: '+47', flag: '🇳🇴', nordic: true },
    { code: 'SE', name: 'Sverige', dial: '+46', flag: '🇸🇪', nordic: true },
    { code: 'DK', name: 'Danmark', dial: '+45', flag: '🇩🇰', nordic: true },
    { code: 'FI', name: 'Finland', dial: '+358', flag: '🇫🇮', nordic: true },
    { code: 'IS', name: 'Island', dial: '+354', flag: '🇮🇸', nordic: true },
    { code: 'GB', name: 'Storbritannia', dial: '+44', flag: '🇬🇧' },
    { code: 'DE', name: 'Tyskland', dial: '+49', flag: '🇩🇪' },
    { code: 'FR', name: 'Frankrike', dial: '+33', flag: '🇫🇷' },
    { code: 'ES', name: 'Spania', dial: '+34', flag: '🇪🇸' },
    { code: 'IT', name: 'Italia', dial: '+39', flag: '🇮🇹' },
    { code: 'NL', name: 'Nederland', dial: '+31', flag: '🇳🇱' },
    { code: 'BE', name: 'Belgia', dial: '+32', flag: '🇧🇪' },
    { code: 'PL', name: 'Polen', dial: '+48', flag: '🇵🇱' },
    { code: 'PT', name: 'Portugal', dial: '+351', flag: '🇵🇹' },
    { code: 'IE', name: 'Irland', dial: '+353', flag: '🇮🇪' },
    { code: 'CH', name: 'Sveits', dial: '+41', flag: '🇨🇭' },
    { code: 'AT', name: 'Østerrike', dial: '+43', flag: '🇦🇹' },
    { code: 'GR', name: 'Hellas', dial: '+30', flag: '🇬🇷' },
    { code: 'EE', name: 'Estland', dial: '+372', flag: '🇪🇪' },
    { code: 'LV', name: 'Latvia', dial: '+371', flag: '🇱🇻' },
    { code: 'LT', name: 'Litauen', dial: '+370', flag: '🇱🇹' },
    { code: 'RU', name: 'Russland', dial: '+7', flag: '🇷🇺' },
    { code: 'UA', name: 'Ukraina', dial: '+380', flag: '🇺🇦' },
    { code: 'TR', name: 'Tyrkia', dial: '+90', flag: '🇹🇷' },
    { code: 'US', name: 'USA', dial: '+1', flag: '🇺🇸' },
    { code: 'AU', name: 'Australia', dial: '+61', flag: '🇦🇺' },
    { code: 'IN', name: 'India', dial: '+91', flag: '🇮🇳' },
    { code: 'PK', name: 'Pakistan', dial: '+92', flag: '🇵🇰' },
    { code: 'TH', name: 'Thailand', dial: '+66', flag: '🇹🇭' },
    { code: 'PH', name: 'Filippinene', dial: '+63', flag: '🇵🇭' },
    { code: 'SO', name: 'Somalia', dial: '+252', flag: '🇸🇴' },
    { code: 'MA', name: 'Marokko', dial: '+212', flag: '🇲🇦' },
    { code: 'SY', name: 'Syria', dial: '+963', flag: '🇸🇾' },
    { code: 'ER', name: 'Eritrea', dial: '+291', flag: '🇪🇷' }
  ];

  var WD = ['søn', 'man', 'tir', 'ons', 'tor', 'fre', 'lør'];
  var WDF = ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'];
  var MON = ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember'];
  var MONA = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];

  // ---- Editable copy --------------------------------------------------------
  // Every user-facing string. A per-section `data-nob-config` JSON blob (built
  // from theme-editor settings in lp-own-booking.liquid) is deep-merged OVER
  // this, so any subset of keys can be overridden and the rest stay in Norwegian.
  var DEFAULTS = {
    steps: ['Tid', 'Detaljer', 'Bekreft'],
    stepLabels: { hero: 'Book gratis prøvetime', choose: 'Book gratis prøvetime', time: 'Velg en tid', details: 'Dine detaljer', confirm: 'Booket', intake: 'Litt om deg og dine mål', intakedone: 'Takk' },
    hero: {
      ratingLine: 'Anbefalt av medlemmene våre på Google',
      chips: ['Gratis · ingen binding', 'Ingen erfaring nødvendig', 'Trygt for vonde ledd og rygg'],
      ctaPrimary: 'Book gratis prøvetime',
      ctaNote: 'Tar under ett minutt · du velger tid selv',
      promiseH: 'Vår lovnad til deg',
      promiseBody: 'Du trenger ikke være i form for å begynne. Vi møter deg der du er, og hjelper deg trygt mot en sterkere og lettere hverdag – i ditt eget tempo. Prøvetimen er gratis, så du har ingenting å tape.',
      reviewsH: 'Hva medlemmene sier',
      ctaSecondary: 'Book din gratis prøvetime'
    },
    time: {
      titleH: 'Velg en tid',
      titleSub: 'Velg en dag, så et ledig tidspunkt.',
      scarcity: 'Maks 2 plasser per prøvetime, i ro og med vertskapet til stede',
      continue: 'Fortsett'
    },
    // «Ring meg»-sporet (2026-08-11): valgsteg med to dører etter hero, pluss
    // en fallback i kalendersteget for folk som blar i dager uten å velge tid.
    // ALT er av som standard — skrus på per seksjon via config-blobben
    // (show_chooser / show_callme_fallback i lp-own-booking.liquid).
    callme: {
      enabled: false,
      fallbackEnabled: false,
      fallbackAfterTaps: 4,
      chooserTitle: 'Hvordan vil du booke prøvetimen?',
      chooserSub: 'Begge er like gratis og like uforpliktende. Velg det som er enklest for deg.',
      doorCallH: 'La oss ringe deg',
      doorCallB: 'Legg igjen nummeret ditt, så ringer vertskapet deg og finner en tid som passer.',
      doorSelfH: 'Jeg finner tid selv',
      doorSelfB: 'Se ledige dager og tider, og velg det som passer deg.',
      formName: 'Fornavn',
      formLast: 'Etternavn',
      formPhone: 'Mobilnummer',
      cta: 'Ring meg opp',
      sending: 'Sender …',
      note: 'Vertskapet ringer deg på dagtid, mandag til fredag. Helt uforpliktende.',
      errName: 'Skriv inn fornavnet ditt',
      errLast: 'Skriv inn etternavnet ditt',
      errPhone: 'Skriv inn et gyldig telefonnummer',
      errSend: 'Klarte ikke å sende. Prøv igjen, eller ring oss direkte.',
      doneH: 'Takk, {first}!',
      doneB: 'Vertskapet ringer deg på dagtid, mandag til fredag. Vil du ikke vente, kan du fortsatt velge en tid selv nedenfor.',
      doneFallbackB: 'Vertskapet ringer deg på dagtid, mandag til fredag, og finner tiden sammen med deg.',
      fallbackQuiet: 'Usikker på hva som passer?',
      fallbackLink: 'Vi kan ringe deg',
      fallbackH: 'Skal vi finne tiden sammen?',
      fallbackB: 'Legg igjen nummeret ditt, så ringer vertskapet deg og finner en tid som faktisk passer.',
      fallbackDismiss: 'Nei takk, jeg velger tid selv'
    },
    details: {
      titleH: 'Dine detaljer',
      summaryK: 'Din time',
      edit: 'Endre',
      submit: 'Bekreft',
      submitting: 'Sender …',
      trust: ['Gratis', 'Ingen binding', 'Du bestemmer tempoet'],
      // Reassurance above the fields. Clarity (Aug): form-openers left silently
      // within 2-12s — the fear is commitment, not the fields. Empty = hidden.
      note: ''
    },
    confirm: {
      thanksNamed: 'Takk, {first}. Vi sees!',
      thanksAnon: 'Vi sees!',
      h1: 'Booket!',
      kSlot: 'Din gratis prøvetime',
      kLocation: 'Sted',
      kRef: 'Referanse',
      status: 'Vi har mottatt bookingen din. Du får bekreftelsen på e-post med en gang.',
      reassure: 'Vi gleder oss til å møte deg. Kom gjerne 5 minutter før, så tar vi imot deg i ro og mak.',
      vervH: 'Har noen tipset deg om oss?',
      vervSub: 'Skriv navnet, så får dere begge en hyggelig overraskelse fra oss. Helt valgfritt.',
      vervPlaceholder: 'Navnet på den som tipset deg',
      vervSend: 'Send',
      vervThanks: 'Takk! Vi hilser videre.',
      intakeH: 'Ett siste steg – så blir timen trygg for deg',
      intakeSub: 'Fortell oss litt om deg og kroppen din. Da vet vi hva du trenger før du kommer, og kan ta godt imot deg – uten overraskelser.',
      intakeCta: 'Fortell oss om deg (1 minutt)',
      contactPrompt: 'Lurer du på noe før timen? Ta gjerne kontakt.',
      restart: 'Tilbake til start'
    },
    intake: {
      bookedPill: 'Timen er booket',
      titleH: 'Litt om deg og dine mål',
      titleSub: 'Svar på noen få spørsmål, så tilpasser vi prøvetimen til akkurat deg. Det tar under ett minutt.',
      qAge: 'Alder',
      qAilments: 'Har du noen fysiske plager, skader eller vondter vi bør ta hensyn til?',
      ailmentsPlaceholder: 'F.eks. artrose, prolaps, byttet kne, fibromyalgi, høyt blodtrykk e.l.',
      qExperience: 'Har du trent styrke før?',
      qReason: 'Hva var den viktigste grunnen til at du booket hos oss?',
      optionalTag: '(valgfritt)',
      manyTag: '(velg gjerne flere)',
      submit: 'Send svar',
      skip: 'Hopp over – du er ferdig',
      alder: ['Under 50 år', '50–59 år', '60–69 år', '70+ år'],
      erfaring: ['Har nesten aldri trent styrke (og gruer meg kanskje litt)', 'Har trent før, men det er veldig lenge siden!', 'Har trent litt av og til', 'Trener jevnlig i dag'],
      grunner: ['At maskinene er trygge for vonde ledd og rygger', 'At det er et rolig miljø uten kroppspress', 'At en komplett styrkeøkt er ferdig på under 40 minutter', 'At jeg kan trene i vanlige hverdagsklær', 'Annet (f.eks. anbefalt av en venn)']
    },
    done: {
      h1: 'Takk!',
      body: 'Svarene hjelper oss å gjøre timen din trygg og god. Vi gleder oss til å se deg i [sted].',
      restart: 'Ferdig'
    },
    reviews: [
      { text: 'Jeg er så takknemlig for å ha oppdaget Nono. Det hjelper meg å komme i form etter en operasjon. Livskvaliteten bedres og humøret stiger. Veldig hyggelig miljø og verdifull veiledning av kompetente folk. Hilsen Marianne', name: 'Marianne Milda Ødegaard', meta: 'Google · august 2026' },
      { text: 'Jeg liker at jeg må ta i litt mer enn jeg er vant med — apparatene har 1–10 i styrke, så jeg kan tilpasse hvert apparat til meg. Trives veldig godt hos dere.', name: 'Karin Solum', meta: 'Google · juli 2026' },
      { text: 'Alltid veldig hyggelig å ta turen innom her. De ansatte står på og gir god service for at man skal føle seg velkommen.', name: 'Thor Pedersen', meta: 'Google · juni 2026' }
    ]
  };

  function isPlainObj(x) { return x && typeof x === 'object' && !Array.isArray(x); }
  // Pure deep-merge: returns a NEW object; `over` wins; nested plain objects
  // recurse; arrays/scalars replace wholesale. Never mutates DEFAULTS.
  function deepMerge(base, over) {
    if (over === undefined || over === null) return base;
    if (isPlainObj(base) && isPlainObj(over)) {
      var out = {};
      Object.keys(base).forEach(function (k) { out[k] = deepMerge(base[k], over[k]); });
      Object.keys(over).forEach(function (k) { if (!(k in base)) out[k] = over[k]; });
      return out;
    }
    return over;
  }

  // ---- helpers -------------------------------------------------------------
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function parseCookies() {
    var out = {};
    try {
      (document.cookie || '').split(';').forEach(function (p) {
        var i = p.indexOf('=');
        if (i > -1) out[p.slice(0, i).trim()] = decodeURIComponent(p.slice(i + 1).trim());
      });
    } catch (e) {}
    return out;
  }
  // --- First-party ad-attribution persistence -------------------------------
  // Meta's _fbc cookie persists the click-id but NOT the ad-landing URL (UTM /
  // ad name), so a booking made on a later or bare-URL visit loses which ad it
  // came from. We persist the original ad-landing (URL + fbclid) in a 90-day
  // first-party cookie — last-touch: each new ad-landing overwrites, bare URLs
  // never overwrite — so a bare-URL booking still attaches the originating ad.
  function hasAdParams(url) {
    try {
      var q = new URL(url).searchParams;
      return !!(q.get('fbclid') || q.get('utm_source') || q.get('utm_campaign') || q.get('utm_content') || q.get('ad_id'));
    } catch (e) { return false; }
  }
  function setCookie(name, val, days) {
    try {
      var d = new Date(); d.setTime(d.getTime() + days * 864e5);
      document.cookie = name + '=' + encodeURIComponent(val) + ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
    } catch (e) {}
  }
  // Returns { landing, fbclid }. On an ad-landing it persists the cookie;
  // otherwise it reads the stored one. Safe to call on load and at booking time.
  function adAttr() {
    var here = window.location.href;
    if (hasAdParams(here)) {
      var fbc = null;
      try { fbc = new URL(here).searchParams.get('fbclid'); } catch (e) {}
      var rec = { landing: here.slice(0, 2048), fbclid: fbc, ts: Date.now() };
      setCookie('nono_attr', JSON.stringify(rec), 90);
      return rec;
    }
    var raw = parseCookies().nono_attr;
    if (raw) { try { var r = JSON.parse(raw); if (r && r.landing) return r; } catch (e) {} }
    return { landing: null, fbclid: null };
  }
  function fbParams() {
    var c = parseCookies();
    var attr = adAttr();
    var fbclid = null;
    try { fbclid = new URL(window.location.href).searchParams.get('fbclid'); } catch (e) {}
    if (!fbclid) fbclid = attr.fbclid || null; // recover the click-id from the stored ad-landing
    return { fbp: c._fbp || null, fbc: c._fbc || null, fbclid: fbclid, landing: attr.landing };
  }
  // ---- funnel beacons (ads.nono.no -> Kalender) ----------------------------
  // Fire-and-forget session analytics for the funnel INSIDE this widget
  // (modal open -> calendar -> slot -> details -> submit -> booked/closed).
  // Every call is fully try/catch'd and the fetch result is ignored: a beacon
  // failure can NEVER touch the booking flow. Carries no name/phone/email —
  // the person lives on the booking row `refCode` joins to server-side.
  var TRK = (function () {
    var sid = null, api = null;
    try {
      var KEY = 'nono_bk_sid';
      try { sid = sessionStorage.getItem(KEY) || null; } catch (e) {}
      if (!sid) {
        sid = (window.crypto && window.crypto.randomUUID)
          ? window.crypto.randomUUID()
          : 'b-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
        // Storage may be blocked — keep the in-memory sid (per-pageview then).
        try { sessionStorage.setItem(KEY, sid); } catch (e) {}
      }
    } catch (e) {}
    function quizSid() {
      try { return sessionStorage.getItem('nono_quiz_sid') || null; } catch (e) { return null; }
    }
    function setApi(endpoint) { if (endpoint && !api) api = endpoint; }
    function send(event, extra) {
      try {
        if (!api || !sid) return;
        var fb = fbParams();
        var body = {
          sessionId: sid,
          event: event,
          quizSessionId: quizSid(),
          eventSourceUrl: window.location.href,
          referrer: document.referrer || null,
          fbclid: fb.fbclid,
          fbp: fb.fbp,
          fbc: fb.fbc
        };
        if (extra) { for (var k in extra) { if (Object.prototype.hasOwnProperty.call(extra, k)) body[k] = extra[k]; } }
        fetch(api + '/api/booking-event', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          keepalive: true,
          credentials: 'omit'
        }).catch(function () {});
      } catch (e) {}
    }
    return { send: send, setApi: setApi };
  })();

  // Nono-dugnaden: capture a ?ref= share code (from a member's link or the A6
  // card's QR) and persist it for the session, so the booking POST can carry
  // it. Invisible plumbing — renders nothing, changes no step, never blocks.
  var VERV = (function () {
    var KEY = 'nono_verv_ref';
    var code = null;
    try {
      var m = /[?&]ref=([A-Za-z0-9-]{3,24})/.exec(window.location.search || '');
      if (m) { code = m[1].toUpperCase(); try { sessionStorage.setItem(KEY, code); } catch (e) {} }
      if (!code) { try { code = sessionStorage.getItem(KEY) || null; } catch (e) {} }
    } catch (e) {}
    return { code: function () { return code; } };
  })();

  function osloHHMM(iso) {
    try {
      return new Intl.DateTimeFormat('nb-NO', {
        timeZone: 'Europe/Oslo', hour: '2-digit', minute: '2-digit', hour12: false
      }).format(new Date(iso));
    } catch (e) {
      return String(iso).slice(11, 16);
    }
  }
  function fmtDayKey(key) {
    // key = 'YYYY-MM-DD' (already Europe/Oslo from the API)
    var p = String(key).split('-');
    var d = new Date(Date.UTC(+p[0], +p[1] - 1, +p[2]));
    var wd = WDF[d.getUTCDay()];
    return wd.charAt(0).toUpperCase() + wd.slice(1) + ' ' + (+p[2]) + '. ' + MON[+p[1] - 1];
  }
  function dayNum(key) { return +String(key).split('-')[2]; }
  function monOf(key) { return MONA[(+String(key).split('-')[1]) - 1] || ''; } // 'jun','jul' (uppercased in CSS)
  function dowOf(key) {
    var p = String(key).split('-');
    return new Date(Date.UTC(+p[0], +p[1] - 1, +p[2])).getUTCDay();
  }

  function init(root) {
    if (!root || root.dataset.bound === '1') return;
    root.dataset.bound = '1';

    var endpoint = (root.getAttribute('data-endpoint') || '').replace(/\/$/, '');
    TRK.setApi(endpoint);
    var cfg = {
      clinic: root.getAttribute('data-clinic') || 'Moss',
      location: root.getAttribute('data-location') || 'Kongens gate 25A, Moss',
      // Directions link shown on the CONFIRMATION screen only (after the
      // decision — never an exit ramp before it). Empty attribute hides it.
      mapsUrl: root.getAttribute('data-maps-url') || '',
      phone: root.getAttribute('data-phone') || '+4740600235',
      phoneText: root.getAttribute('data-phone-text') || '406 00 235',
      email: root.getAttribute('data-email') || ['hei', 'nono.no'].join('@'),
      heading: root.getAttribute('data-heading') || 'Det er aldri for sent å bli sterkere',
      sub: root.getAttribute('data-sub') || 'Rolig styrketrening for deg over 50 – trygt, uten kroppspress, og tilpasset akkurat din kropp.',
      startStep: root.getAttribute('data-start-step') || 'hero',
      showHero: root.getAttribute('data-show-hero') !== 'false',
      heroImage: root.getAttribute('data-hero-image') || '',
      heroImageAlt: root.getAttribute('data-hero-image-alt') || '',
      // Nono-dugnaden: post-conversion tip question on the Booket! screen.
      vervHint: root.getAttribute('data-verv-hint') === 'true'
    };
    // Editor-overridable copy: DEFAULTS deep-merged with the per-section blob.
    // Malformed/empty blob → pure DEFAULTS (the widget can never render blank).
    var copy = DEFAULTS;
    try {
      var raw = root.getAttribute('data-nob-config');
      if (raw) { var over = JSON.parse(raw); if (isPlainObj(over)) copy = deepMerge(DEFAULTS, over); }
    } catch (e) {}
    var inModal = !!(root.closest && root.closest('.nob-modal'));

    var st = {
      step: cfg.startStep, // justeres til baseStart() under når hero er av
      days: null,        // [{date, weekday, remaining, slots:[{startsAt,capacity,remaining}]}]
      loading: false,
      loadError: false,
      selDay: null,      // date key
      selSlot: null,     // ISO
      firstName: '', lastName: '', phone: '', email: '',
      ccCode: 'NO', ccOpen: false, ccQuery: '',
      tried: false,
      ref: null,
      iAlder: '', iPlager: '', iErfaring: '', iGrunner: [], iTried: false,
      submitting: false, submitErr: '',
      // «Ring meg»-sporet: deler firstName/phone med detalj-skjemaet (samme
      // data-f-bindinger), så prefill fra quizen virker begge steder.
      cmTried: false, cmSending: false, cmErr: '', cmDone: false, cmFrom: '',
      fbShown: false, fbDismissed: false, dayTaps: 0
    };
    // Er valgsteget/fallbacken på for denne seksjonen? (config-blobben avgjør)
    var CM = copy.callme || DEFAULTS.callme;
    function chooserOn() { return !!CM.enabled; }
    function fallbackOn() { return !!CM.fallbackEnabled; }
    // Startsteget når hero er av: chooseren skal ikke bli en stille no-op.
    function baseStart() { return cfg.showHero ? 'hero' : (chooserOn() ? 'choose' : 'time'); }
    // Én chooser_view per åpning, uansett hvilken vei folk kom inn.
    function trackChooserView() {
      try {
        if (st.chSeen) return;
        var mEl = root.closest ? root.closest('.nob-modal') : null;
        if (mEl && mEl.hidden) return;
        st.chSeen = true;
        TRK.send('chooser_view', { stepName: 'choose' });
      } catch (e) {}
    }

    // ---- prefill (same-tab handoff from the quiz) --------------------------
    // The treningskalkulator quiz stores the visitor's contact details under
    // sessionStorage['nono_prefill_customer'] = {firstName,lastName,phone} when
    // they submit the contact step. We prefill the details form from it so they
    // never type the same information twice. sessionStorage is same-tab only
    // and dies with the tab — no cross-user or cross-visit leakage.
    function normalizePrefillPhone(raw) {
      var p = String(raw || '').trim();
      // The details form holds the NATIONAL number (the country code is added at
      // submit as country().dial + ' ' + phone), so strip a Norwegian prefix:
      // "+47 ...", "0047...", or a bare leading 47 when 10 digits total.
      p = p.replace(/^\+47[\s\-]?/, '').replace(/^0047[\s\-]?/, '');
      var digits = p.replace(/\D/g, '');
      if (digits.length === 10 && digits.indexOf('47') === 0) digits = digits.slice(2);
      return digits;
    }
    function applyPrefill() {
      try {
        var raw = sessionStorage.getItem('nono_prefill_customer');
        if (!raw) return;
        var pf = JSON.parse(raw);
        if (!pf || typeof pf !== 'object') return;
        if (typeof pf.firstName === 'string' && pf.firstName.trim()) st.firstName = pf.firstName.trim();
        if (typeof pf.lastName === 'string' && pf.lastName.trim()) st.lastName = pf.lastName.trim();
        var ph = normalizePrefillPhone(pf.phone);
        if (ph.length >= 6) { st.phone = ph; st.ccCode = 'NO'; }
      } catch (e) {}
    }
    applyPrefill();

    function country() {
      for (var i = 0; i < COUNTRIES.length; i++) if (COUNTRIES[i].code === st.ccCode) return COUNTRIES[i];
      return COUNTRIES[0];
    }
    function validate() {
      return {
        firstName: st.firstName.trim().length >= 1,
        lastName: st.lastName.trim().length >= 1,
        phone: st.phone.replace(/\D/g, '').length >= 6,
        email: /^\S+@\S+\.\S+$/.test(st.email.trim())
      };
    }
    function selDayObj() {
      if (!st.days) return null;
      for (var i = 0; i < st.days.length; i++) if (st.days[i].date === st.selDay) return st.days[i];
      return null;
    }
    function selSummary() {
      if (!st.selDay) return '';
      var base = fmtDayKey(st.selDay);
      return st.selSlot ? base + ' · kl. ' + osloHHMM(st.selSlot) : base;
    }
    // Compact slot label for the step CTAs, e.g. "Tor 26. jun kl. 10:30".
    function fmtSlotShort() {
      if (!st.selDay || !st.selSlot) return '';
      var wd = WD[dowOf(st.selDay)];
      var p = String(st.selDay).split('-');
      return wd.charAt(0).toUpperCase() + wd.slice(1) + ' ' + (+p[2]) + '. ' + MONA[+p[1] - 1] + ' kl. ' + osloHHMM(st.selSlot);
    }
    // Trust row under the confirm CTA: each item a green check.
    function trustRowHTML() {
      var items = (copy.details.trust || []).map(function (t) {
        return '<span class="nob-trust-item"><span class="nob-trust-check">' + ICO.check + '</span>' + esc(t) + '</span>';
      }).join('');
      return '<div class="nob-trust">' + items + '</div>';
    }

    var STEP_LABELS = copy.stepLabels;
    function go(step) {
      st.step = step;
      render(true);
      if (inModal) {
        var modalEl = root.closest('.nob-modal');
        if (modalEl) modalEl.setAttribute('aria-label', STEP_LABELS[step] || copy.stepLabels.hero);
        var panel = root.closest('.nob-modal-panel');
        if (panel) { try { panel.scrollTop = 0; } catch (e) {} }
        return;
      }
      try { root.scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch (e) {}
      try { window.scrollTo(0, Math.max(0, root.getBoundingClientRect().top + window.pageYOffset - 8)); } catch (e) {}
    }

    // ---- data --------------------------------------------------------------
    function trackCalendarView() {
      try {
        if (st.calSeen) return; // once per open
        var mEl = root.closest ? root.closest('.nob-modal') : null;
        if (mEl && mEl.hidden) return;
        var bookable = 0;
        (st.days || []).forEach(function (d) { (d.slots || []).forEach(function (s2) { if ((Number(s2.remaining) || 0) > 0) bookable++; }); });
        st.calSeen = true;
        TRK.send('calendar_view', { stepName: 'time', daysShown: (st.days || []).length, slotsShown: bookable });
      } catch (e) {}
    }

    function loadAvailability() {
      if (!endpoint) { st.loadError = true; render(); return; }
      st.loading = true; st.loadError = false; render();
      fetch(endpoint + '/api/booking/availability', { credentials: 'omit' })
        .then(function (r) { return r.json(); })
        .then(function (j) {
          st.loading = false;
          st.days = (j && j.ok && Array.isArray(j.days)) ? j.days.filter(function (d) { return d.slots && d.slots.length; }) : [];
          try {
            // The widget PRELOADS availability at page load while the modal is
            // still hidden — only beacon when the calendar is actually visible,
            // otherwise every pageview would count as a funnel session.
            var mEl = root.closest ? root.closest('.nob-modal') : null;
            if (!(mEl && mEl.hidden)) {
              if (j && j.ok) {
                // With the offer step in front, data can finish loading while
                // the visitor is still reading it — saw_calendar must mean the
                // TIME STEP was on screen, so the funnel stays honest.
                if (st.step === 'time') trackCalendarView();
              } else {
                // HTTP 5xx with a JSON {ok:false} body parses fine and lands
                // here — that is an OUTAGE, not an empty calendar.
                TRK.send('calendar_error', { stepName: 'time' });
              }
            }
          } catch (e) {}
          if (!st.selDay && st.days.length) st.selDay = st.days[0].date;
          if (st.selDay && !selDayObj() && st.days.length) { st.selDay = st.days[0].date; st.selSlot = null; }
          // Ikke re-rendre valgsteget: ingenting der avhenger av days, og en
          // innerHTML-rebuild midt i skrivingen lukker mobiltastaturet.
          if (st.step !== 'choose') render();
        })
        .catch(function () {
          st.loading = false; st.loadError = true;
          try {
            var mEl2 = root.closest ? root.closest('.nob-modal') : null;
            if (!(mEl2 && mEl2.hidden)) TRK.send('calendar_error', { stepName: 'time' });
          } catch (e) {}
          if (st.step !== 'choose') render();
        });
    }

    function submitBooking() {
      var v = validate();
      if (!(v.firstName && v.lastName && v.phone && v.email)) { st.tried = true; TRK.send('submit_error', { stepName: 'details', errorKind: 'validation' }); render(); return; }
      if (st.submitting) return;
      st.submitting = true; st.submitErr = ''; st.tried = false; render();
      TRK.send('submit_attempt', { stepName: 'details', slotIso: st.selSlot });
      var fb = fbParams();
      var payload = {
        firstName: st.firstName.trim(),
        lastName: st.lastName.trim(),
        phone: country().dial + ' ' + st.phone.trim(),
        email: st.email.trim(),
        slotStartsAt: st.selSlot,
        fbp: fb.fbp || undefined,
        fbc: fb.fbc || undefined,
        fbclid: fb.fbclid || undefined,
        // Prefer the live URL when it carries ad params; else fall back to the
        // stored ad-landing so a later/bare-URL booking keeps its ad source.
        pageUrl: hasAdParams(window.location.href) ? window.location.href : (fb.landing || window.location.href),
        refCode: VERV.code() || undefined
      };
      fetch(endpoint + '/api/booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'omit'
      })
        .then(function (r) { return r.json().then(function (j) { return { status: r.status, j: j }; }); })
        .then(function (res) {
          st.submitting = false;
          if (res.status === 201 && res.j && res.j.ok) {
            st.ref = res.j.ref || null;
            TRK.send('booked', { stepName: 'confirm', refCode: st.ref, slotIso: payload.slotStartsAt });
            firePixelSchedule(st.ref);
            go('confirm');
            return;
          }
          if (res.status === 409) {
            // Slot was taken between view and submit: refresh + send back to time.
            TRK.send('submit_error', { stepName: 'details', errorKind: 'slot_taken' });
            st.submitErr = (res.j && res.j.message) || 'Den tiden ble nettopp tatt – velg en annen.';
            st.selSlot = null;
            st.step = 'time';
            loadAvailability();
            return;
          }
          TRK.send('submit_error', { stepName: 'details', errorKind: 'server' });
          st.submitErr = (res.j && (res.j.message || res.j.error)) || 'Noe gikk galt. Prøv igjen.';
          render();
        })
        .catch(function () {
          st.submitting = false;
          TRK.send('submit_error', { stepName: 'details', errorKind: 'network' });
          st.submitErr = 'Klarte ikke å sende bookingen. Sjekk nettet og prøv igjen.';
          render();
        });
    }

    function firePixelSchedule(ref) {
      try {
        if (window.fbq && ref) {
          window.fbq('track', 'Schedule', { content_category: 'provetime', content_name: 'gratis prøvetime' }, { eventID: ref });
        }
      } catch (e) {}
    }

    // ---- «Ring meg»-sporet -------------------------------------------------
    // Ett felles submit for begge flater (valgsteget og kalender-fallbacken).
    // Leadet går til /api/lead med SAMME kontrakt som quizen (proxy → Momence
    // lead + server-side CAPI 'Lead'), så det lander rett i ringelisten på
    // ads.nono.no. Konteksten («hvorfor ringer vi») reiser i quizSummary —
    // det er feltet vertskapet ser i lista.
    function cmValidate() {
      return {
        name: st.firstName.trim().length >= 1,
        last: st.lastName.trim().length >= 1,
        phone: st.phone.replace(/\D/g, '').length >= 6
      };
    }
    function cmPhoneFull() {
      var p = st.phone.trim();
      if (/^\+/.test(p)) return p.slice(0, 32);
      if (/^00/.test(p)) return ('+' + p.slice(2)).slice(0, 32);
      return (country().dial + ' ' + p).slice(0, 32);
    }
    // Landkodevelgeren i ring-meg-skjemaet gjenbruker detaljskjemaets
    // [data-cc-toggle]/[data-cc-host]-maskineri — wire() binder på selector,
    // og kun ett skjema rendres om gangen, så det kolliderer aldri.
    function cmContext(from) {
      if (from === 'fallback') {
        var seen = st.dayTaps > 0 ? (' Så på ' + st.dayTaps + ' dag' + (st.dayTaps === 1 ? '' : 'er') + ' uten å finne en tid som passet.') : '';
        return 'Ba om å bli ringt fra kalenderen i bookingvinduet.' + seen + ' Ring og finn tiden sammen.\nRinges: Ja, gjerne';
      }
      return 'Valgte «' + CM.doorCallH + '» i bookingvinduet. Ring og finn tiden sammen.\nRinges: Ja, gjerne';
    }
    function submitCallme(from) {
      var v = cmValidate();
      if (!(v.name && v.last && v.phone)) {
        st.cmTried = true; render();
        var errIn = root.querySelector('.nob-input.is-err');
        if (errIn) { try { errIn.focus({ preventScroll: true }); } catch (e) {} }
        return;
      }
      if (st.cmSending) return;
      st.cmSending = true; st.cmErr = ''; st.cmTried = false; st.cmFrom = from; render();
      TRK.send('callback_request', { stepName: st.step });
      var fb = fbParams();
      var quizSid = null;
      try { quizSid = sessionStorage.getItem('nono_quiz_sid') || null; } catch (e) {}
      var payload = {
        firstName: st.firstName.trim().slice(0, 100),
        lastName: st.lastName.trim().slice(0, 100),
        phoneNumber: cmPhoneFull(),
        consent: true,
        discoveryAnswer: 'Ring meg (bookingvinduet)',
        quizSummary: cmContext(from),
        quizSessionId: quizSid || undefined,
        pageUrl: hasAdParams(window.location.href) ? window.location.href : (fb.landing || window.location.href),
        hp: ''
      };
      fetch(endpoint + '/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
        credentials: 'omit'
      })
        .then(function (r) {
          st.cmSending = false;
          if (r && r.ok) {
            st.cmDone = true; st.fbShown = false;
            // Sesjonsminne: hindrer dobbel-lead hvis vinduet åpnes på nytt.
            try { sessionStorage.setItem('nono_cm_done', '1'); } catch (e2) {}
            render();
            return;
          }
          st.cmErr = CM.errSend;
          // Feilen må være synlig selv om brukeren har navigert videre til
          // kalenderen mens kallet var underveis — tving kortet åpent der.
          if (st.step === 'time' && !st.selSlot) st.fbShown = true;
          render();
        })
        .catch(function () {
          st.cmSending = false;
          st.cmErr = CM.errSend;
          if (st.step === 'time' && !st.selSlot) st.fbShown = true;
          render();
        });
    }
    // Skjema-innmaten (navn + telefon + CTA) — gjenbrukes i begge flater.
    function cmFormHTML() {
      var v = cmValidate(), t = st.cmTried;
      var nameErr = t && !v.name, lastErr = t && !v.last, phoneErr = t && !v.phone;
      var c = country();
      var html = '';
      html += '<label class="nob-field"><span class="nob-label">' + esc(CM.formName) + '</span>'
        + '<input class="nob-input' + (nameErr ? ' is-err' : '') + '" data-f="firstName" value="' + esc(st.firstName) + '" inputmode="text" autocomplete="given-name" placeholder="' + esc(CM.formName) + '"' + (nameErr ? ' aria-invalid="true" aria-describedby="nob-err-cm-name"' : '') + '>'
        + (nameErr ? '<span class="nob-err" id="nob-err-cm-name" role="alert">' + esc(CM.errName) + '</span>' : '') + '</label>';
      html += '<label class="nob-field"><span class="nob-label">' + esc(CM.formLast) + '</span>'
        + '<input class="nob-input' + (lastErr ? ' is-err' : '') + '" data-f="lastName" value="' + esc(st.lastName) + '" inputmode="text" autocomplete="family-name" placeholder="' + esc(CM.formLast) + '"' + (lastErr ? ' aria-invalid="true" aria-describedby="nob-err-cm-last"' : '') + '>'
        + (lastErr ? '<span class="nob-err" id="nob-err-cm-last" role="alert">' + esc(CM.errLast) + '</span>' : '') + '</label>';
      html += '<label class="nob-field"><span class="nob-label">' + esc(CM.formPhone) + '</span><div class="nob-phone">'
        + '<button type="button" class="nob-cc-btn' + (phoneErr ? ' is-err' : '') + '" data-cc-toggle aria-haspopup="listbox" aria-expanded="' + (st.ccOpen ? 'true' : 'false') + '" aria-label="Landskode, valgt ' + esc(c.name) + '"><span class="nob-cc-flag" aria-hidden="true">' + c.flag + '</span><span>' + c.dial + '</span><span class="nob-cc-caret" aria-hidden="true">▾</span></button>'
        + '<input class="nob-input nob-phone-input' + (phoneErr ? ' is-err' : '') + '" data-f="phone" value="' + esc(st.phone) + '" inputmode="tel" autocomplete="tel" placeholder="' + esc(CM.formPhone) + '" aria-label="' + esc(CM.formPhone) + '"' + (phoneErr ? ' aria-invalid="true" aria-describedby="nob-err-cm-phone"' : '') + '>'
        + '<div class="nob-cc-menu-host" data-cc-host></div>'
        + '</div>' + (phoneErr ? '<span class="nob-err" id="nob-err-cm-phone" role="alert">' + esc(CM.errPhone) + '</span>' : '') + '</label>';
      if (st.cmErr) html += '<div class="nob-banner-err">' + esc(st.cmErr) + ' <a href="tel:' + esc(cfg.phone) + '">' + esc(cfg.phoneText) + '</a></div>';
      html += '<button type="button" class="nob-cta nob-cta--call" data-callme-submit' + (st.cmSending ? ' disabled' : '') + '>' + esc(st.cmSending ? CM.sending : CM.cta) + '</button>';
      html += '<p class="nob-callme-note">' + esc(CM.note) + '</p>';
      return html;
    }
    function cmDoneHTML(bodyText) {
      var first = st.firstName.trim();
      var h = first ? esc(CM.doneH).split('{first}').join(esc(first)) : 'Takk!';
      return '<div class="nob-callme-done" role="status"><span class="nob-callme-done-check">' + ICO.check + '</span>'
        + '<div><span class="nob-callme-done-h">' + h + '</span>'
        + '<p class="nob-callme-done-b">' + esc(bodyText) + '</p></div></div>';
    }
    // Valgsteget: to dører — ring-meg (service) og finn-tid-selv.
    function chooserHTML() {
      var body = '<div class="nob-screen">';
      var backTarget = cfg.showHero ? 'hero' : null;
      body += '<div class="nob-head">'
        + (backTarget ? '<button type="button" class="nob-back" data-choose-back aria-label="Tilbake">' + ICO.back + '</button>' : '')
        + '<div class="nob-title" style="flex:1"><h2>' + esc(CM.chooserTitle) + '</h2><p>' + esc(CM.chooserSub) + '</p></div></div>';
      body += '<div class="nob-doors">';
      // Dør 1: ring meg (service-døren)
      body += '<div class="nob-door nob-door--call">';
      if (st.cmDone) {
        body += cmDoneHTML(CM.doneB);
      } else if (st.cmOpen) {
        body += '<span class="nob-door-h"><span class="nob-door-ico">' + ICO.phone + '</span>' + esc(CM.doorCallH) + '</span>'
          + '<p class="nob-door-b">' + esc(CM.doorCallB) + '</p>'
          + cmFormHTML();
      } else {
        // SAMMENSLÅTT som standard. Utfoldet tok skjemaet 580 px av 812 —
        // dør 2 ble 48 % synlig, og garantien og anmeldelsene under falt helt
        // ut av skjermen. Det brøt kodens egen regel litt lenger ned:
        // «Ingenting blokkerer dørene.» Ett trykk folder den ut.
        body += '<button type="button" class="nob-door-open" data-choose-call>'
          + '<span class="nob-door-h"><span class="nob-door-ico">' + ICO.phone + '</span>' + esc(CM.doorCallH) + '</span>'
          + '<p class="nob-door-b">' + esc(CM.doorCallB) + '</p>'
          + '</button>';
      }
      body += '</div>';
      // Dør 2: finn tid selv
      body += '<button type="button" class="nob-door nob-door--self" data-choose-self>'
        + '<span class="nob-door-h">' + esc(CM.doorSelfH) + ' <span aria-hidden="true">→</span></span>'
        + '<span class="nob-door-b">' + esc(CM.doorSelfB) + '</span>'
        + '</button>';
      body += '</div>';
      // Lesestoff UNDER dørene (Clarity aug-25: tilbudssiden ble grundig LEST,
      // særlig anmeldelsene — men som eget steg kostet den 15-20 % av
      // åpningene). Her: de som er klare handler øverst, de som trenger
      // overbevisning scroller og leser. Ingenting blokkerer dørene.
      if (copy.hero && copy.hero.promiseBody) {
        body += '<div class="nob-promise"><span class="nob-promise-h">' + esc(copy.hero.promiseH) + '</span><p>' + esc(copy.hero.promiseBody) + '</p></div>';
      }
      var chRevs = (copy.reviews || []).map(function (r) {
        return '<div class="nob-review"><span class="nob-stars">★★★★★</span><p>“' + esc(r.text) + '”</p>'
          + '<div class="nob-review-by"><span class="nob-review-name">' + esc(r.name) + '</span><span class="nob-review-meta">' + esc(r.meta) + '</span></div></div>';
      }).join('');
      if (chRevs) {
        body += '<div class="nob-reviews"><span class="nob-reviews-h">' + esc(copy.hero.reviewsH) + '</span>' + chRevs + '</div>';
      }
      body += '</div>';
      return { html: body };
    }
    // Fallback-kortet i kalendersteget (vises etter N dagbytter uten valg,
    // eller når den stille lenken åpnes).
    function fbCardHTML() {
      if (st.cmDone) return cmDoneHTML(CM.doneFallbackB);
      return '<div class="nob-door nob-door--call nob-fbcard">'
        + '<span class="nob-door-h"><span class="nob-door-ico">' + ICO.phone + '</span>' + esc(CM.fallbackH) + '</span>'
        + '<p class="nob-door-b">' + esc(CM.fallbackB) + '</p>'
        + cmFormHTML()
        + '<button type="button" class="nob-link" data-callme-dismiss>' + esc(CM.fallbackDismiss) + '</button>'
        + '</div>';
    }

    function submitIntake() {
      var ok = !!st.iErfaring && st.iGrunner.length > 0;
      if (!ok) { st.iTried = true; render(); return; }
      TRK.send('intake_submit', { stepName: 'intake' });
      var intake = { alder: st.iAlder || null, plager: st.iPlager || null, erfaring: st.iErfaring, grunner: st.iGrunner };
      if (endpoint && st.ref) {
        fetch(endpoint + '/api/booking/intake', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ref: st.ref, intake: intake }),
          keepalive: true,
          credentials: 'omit'
        }).catch(function () {});
      }
      go('intakedone');
    }

    function cmReset() {
      st.cmTried = false; st.cmSending = false; st.cmErr = ''; st.cmFrom = '';
      st.fbShown = false; st.fbDismissed = false; st.dayTaps = 0; st.chSeen = false;
      // «Allerede bedt om å bli ringt» overlever gjenåpning i samme fane, så
      // samme person aldri sender to leads ved et uhell.
      var done = false;
      try { done = sessionStorage.getItem('nono_cm_done') === '1'; } catch (e) {}
      st.cmDone = done;
    }
    cmReset();

    function restart() {
      st.step = baseStart();
      st.selSlot = null; st.firstName = ''; st.lastName = ''; st.phone = ''; st.email = '';
      applyPrefill();
      st.tried = false; st.ref = null; st.submitErr = '';
      st.iAlder = ''; st.iPlager = ''; st.iErfaring = ''; st.iGrunner = []; st.iTried = false;
      cmReset();
      if (st.days && st.days.length) st.selDay = st.days[0].date;
      go(st.step);
      if (!st.days) loadAvailability();
    }

    // ---- step renderers ----------------------------------------------------
    function stepsBar(activeIdx, backFn) {
      // activeIdx: 1=time, 2=details, 3=confirm
      var labels = copy.steps;
      var html = '<div class="nob-head">';
      html += '<button type="button" class="nob-back" aria-label="Tilbake">' + ICO.back + '</button>';
      html += '<div class="nob-steps">';
      for (var i = 0; i < labels.length; i++) {
        var n = i + 1, done = n < activeIdx, cur = n === activeIdx;
        var cls = 'nob-step' + (done ? ' is-done' : '') + (cur ? ' is-cur' : '');
        html += '<div class="' + cls + '"><span class="nob-step-dot">' + (done ? ICO.check : n) + '</span><span class="nob-step-lbl">' + esc(labels[i]) + '</span></div>';
        if (n < labels.length) html += '<span class="nob-step-line' + (n < activeIdx ? ' is-on' : '') + '"></span>';
      }
      html += '</div></div>';
      return { html: html, wire: function (el) { var b = el.querySelector('.nob-back'); if (b) b.addEventListener('click', backFn); } };
    }

    function heroHTML() {
      var revs = (copy.reviews || []).map(function (r) {
        return '<div class="nob-review"><span class="nob-stars">★★★★★</span><p>“' + esc(r.text) + '”</p>'
          + '<div class="nob-review-by"><span class="nob-review-name">' + esc(r.name) + '</span><span class="nob-review-meta">' + esc(r.meta) + '</span></div></div>';
      }).join('');
      var heroImg = cfg.heroImage
        ? '<div class="nob-hero-img"><img src="' + esc(cfg.heroImage) + '" alt="' + esc(cfg.heroImageAlt) + '" loading="lazy"></div>'
        : '';
      var chips = (copy.hero.chips || []).map(function (c) { return '<span class="nob-chip">' + esc(c) + '</span>'; }).join('');
      return ''
        + '<div class="nob-screen nob-hero">'
        + '  <div class="nob-brandrow"><span class="nob-brand">nono</span><span class="nob-clinic">' + esc(cfg.clinic) + '</span></div>'
        + heroImg
        + '  <div class="nob-rating"><span class="nob-stars">★★★★★</span><span class="nob-rating-txt">' + esc(copy.hero.ratingLine) + '</span></div>'
        + '  <div class="nob-herohead"><h1>' + esc(cfg.heading) + '</h1><p>' + esc(cfg.sub) + '</p></div>'
        + '  <div class="nob-chips">' + chips + '</div>'
        + '  <div class="nob-cta-wrap"><button type="button" class="nob-cta" data-go-time>' + esc(copy.hero.ctaPrimary) + '</button><span class="nob-cta-note">' + esc(copy.hero.ctaNote) + '</span></div>'
        + '  <div class="nob-promise"><span class="nob-promise-h">' + esc(copy.hero.promiseH) + '</span><p>' + esc(copy.hero.promiseBody) + '</p></div>'
        + '  <div class="nob-reviews"><span class="nob-reviews-h">' + esc(copy.hero.reviewsH) + '</span>' + revs + '</div>'
        + '  <button type="button" class="nob-cta" data-go-time>' + esc(copy.hero.ctaSecondary) + '</button>'
        + '</div>';
    }

    function timeHTML() {
      var bar = stepsBar(1, function () { go(chooserOn() ? 'choose' : (cfg.showHero ? 'hero' : 'time')); });
      var body = '';
      body += '<div class="nob-screen">';
      body += bar.html;
      body += '<div class="nob-title"><h2>' + esc(copy.time.titleH) + '</h2><p>' + esc(copy.time.titleSub) + '</p></div>';
      body += '<div class="nob-scarcity"><span class="nob-scar-dot"></span>' + esc(copy.time.scarcity) + '</div>';
      if (st.submitErr) body += '<div class="nob-banner-err">' + esc(st.submitErr) + '</div>';

      if (st.loading && !st.days) {
        body += '<div class="nob-loading"><span class="nob-spin"></span><span>Henter ledige tider …</span></div>';
      } else if (st.loadError) {
        body += '<div class="nob-loading"><p>Vi klarte ikke å hente ledige tider akkurat nå.</p><button type="button" class="nob-ghost" data-retry>Prøv igjen</button>'
          + '<p class="nob-cta-note">Eller ring oss på <a href="tel:' + esc(cfg.phone) + '">' + esc(cfg.phoneText) + '</a></p></div>';
      } else if (!st.days || !st.days.length) {
        body += '<div class="nob-loading"><p>Ingen ledige tider akkurat nå. Ring oss gjerne på <a href="tel:' + esc(cfg.phone) + '">' + esc(cfg.phoneText) + '</a>, så finner vi en tid sammen.</p></div>';
      } else {
        // day strip
        body += '<div class="nob-daystrip" data-strip role="group" aria-label="Velg dag">';
        st.days.forEach(function (d) {
          var sel = d.date === st.selDay;
          // Each box reads weekday + day-number + MONTH (cleaner than a count).
          // The per-day "X ledige" tally was removed — scarcity lives in the
          // banner above ("Maks 2 plasser per time") and per-slot "Opptatt" on
          // the time step, so the boxes stay calm and legible.
          body += '<button type="button" class="nob-day' + (sel ? ' is-sel' : '') + '" data-day="' + esc(d.date) + '"'
            + ' aria-pressed="' + (sel ? 'true' : 'false') + '" aria-label="' + esc(fmtDayKey(d.date)) + '">'
            + '<span class="nob-day-wd" aria-hidden="true">' + WD[dowOf(d.date)] + '</span>'
            + '<span class="nob-day-n" aria-hidden="true">' + dayNum(d.date) + '</span>'
            + '<span class="nob-day-mo" aria-hidden="true">' + monOf(d.date) + '</span></button>';
        });
        body += '</div>';
        body += '<div class="nob-strip-nav"><button type="button" class="nob-ghost" data-scroll="-1">‹ Tidligere</button><button type="button" class="nob-ghost" data-scroll="1">Senere ›</button></div>';

        // slots for selected day
        var day = selDayObj();
        body += '<div class="nob-slotshead"><span>' + esc(selSummary() ? fmtDayKey(st.selDay) : '') + '</span><span class="nob-muted">Opptatt = allerede booket</span></div>';
        body += '<div class="nob-slots" role="group" aria-label="Velg tid">';
        (day ? day.slots : []).forEach(function (s) {
          var remn = Number(s.remaining) || 0;
          var taken = remn <= 0;
          var sel = !taken && st.selSlot === s.startsAt;
          // A half-booked slot looks exactly like a free one: no pink, no
          // "1 ledig". Only a full slot is marked, because only that one
          // cannot be picked.
          var cls = 'nob-slot' + (taken ? ' is-taken' : '') + (sel ? ' is-sel' : '');
          var sub = taken ? 'Opptatt' : '';
          var hhmm = osloHHMM(s.startsAt);
          var aria = 'kl. ' + hhmm + (taken ? ', opptatt' : ', ledig');
          body += '<button type="button" class="' + cls + '" aria-label="' + esc(aria) + '"'
            + (taken ? ' disabled aria-disabled="true"' : ' data-slot="' + esc(s.startsAt) + '" aria-pressed="' + (sel ? 'true' : 'false') + '"') + '>'
            + '<span class="nob-slot-t" aria-hidden="true">' + hhmm + '</span>'
            + (sub ? '<span class="nob-slot-sub" aria-hidden="true">' + sub + '</span>' : '') + '</button>';
        });
        body += '</div>';
        // «Ring meg»-fallbacken: kortet vises når jeger-telleren utløser det
        // eller den stille lenken åpnes — aldri når en tid alt er valgt.
        if (fallbackOn() && !st.selSlot) {
          if (st.fbShown || st.cmDone) {
            body += fbCardHTML();
          } else if (!st.fbDismissed) {
            body += '<div class="nob-quietline">' + esc(CM.fallbackQuiet) + ' <button type="button" class="nob-link" data-callme-open>' + esc(CM.fallbackLink) + '</button></div>';
          }
        }
        var hasSlot = !!st.selSlot;
        var contLbl = hasSlot
          ? (esc(copy.time.continue) + ' · ' + esc(fmtSlotShort()) + ' →')
          : esc(copy.time.continue);
        // Button is always present (inline, scroll-reachable) before a slot is
        // chosen so it never blocks the slots; once a time is picked it flips to
        // a pinned sticky footer (is-sticky) so people who don't scroll still see
        // Fortsett. Slot-click re-renders → adds is-sticky + enables the button.
        body += '<div class="nob-cta-wrap' + (hasSlot ? ' is-sticky' : '') + '"><button type="button" class="nob-cta' + (hasSlot ? ' nob-cta--dated' : ' is-disabled') + '" data-go-details' + (hasSlot ? '' : ' disabled') + '>' + contLbl + '</button>' + trustRowHTML() + '</div>';
      }
      body += '</div>';
      return { html: body, wireBar: bar.wire };
    }

    function detailsHTML() {
      var bar = stepsBar(2, function () { go('time'); });
      var v = validate(), t = st.tried;
      var firstErr = t && !v.firstName, lastErr = t && !v.lastName, phoneErr = t && !v.phone, emailErr = t && !v.email;
      var c = country();
      var body = '<div class="nob-screen">';
      body += bar.html;
      body += '<h2 class="nob-title-solo">' + esc(copy.details.titleH) + '</h2>';
      body += '<div class="nob-summary"><div><span class="nob-summary-k">' + esc(copy.details.summaryK) + '</span><span class="nob-summary-v">' + esc(selSummary()) + '</span></div><button type="button" class="nob-link" data-edit>' + esc(copy.details.edit) + '</button></div>';
      if (copy.details.note) body += '<p class="nob-note">' + esc(copy.details.note) + '</p>';

      body += '<label class="nob-field"><span class="nob-label">Fornavn</span>'
        + '<input class="nob-input' + (firstErr ? ' is-err' : '') + '" data-f="firstName" value="' + esc(st.firstName) + '" inputmode="text" autocomplete="given-name" placeholder="Fornavn"' + (firstErr ? ' aria-invalid="true" aria-describedby="nob-err-first"' : '') + '>'
        + (firstErr ? '<span class="nob-err" id="nob-err-first" role="alert">Skriv inn fornavnet ditt</span>' : '') + '</label>';

      body += '<label class="nob-field"><span class="nob-label">Etternavn</span>'
        + '<input class="nob-input' + (lastErr ? ' is-err' : '') + '" data-f="lastName" value="' + esc(st.lastName) + '" inputmode="text" autocomplete="family-name" placeholder="Etternavn"' + (lastErr ? ' aria-invalid="true" aria-describedby="nob-err-last"' : '') + '>'
        + (lastErr ? '<span class="nob-err" id="nob-err-last" role="alert">Skriv inn etternavnet ditt</span>' : '') + '</label>';

      body += '<label class="nob-field"><span class="nob-label">Telefon</span><div class="nob-phone">'
        + '<button type="button" class="nob-cc-btn' + (phoneErr ? ' is-err' : '') + '" data-cc-toggle aria-haspopup="listbox" aria-expanded="' + (st.ccOpen ? 'true' : 'false') + '" aria-label="Landskode, valgt ' + esc(c.name) + '"><span class="nob-cc-flag" aria-hidden="true">' + c.flag + '</span><span>' + c.dial + '</span><span class="nob-cc-caret" aria-hidden="true">▾</span></button>'
        + '<input class="nob-input nob-phone-input' + (phoneErr ? ' is-err' : '') + '" data-f="phone" value="' + esc(st.phone) + '" inputmode="tel" autocomplete="tel" placeholder="Mobilnummer" aria-label="Mobilnummer"' + (phoneErr ? ' aria-invalid="true" aria-describedby="nob-err-phone"' : '') + '>'
        + '<div class="nob-cc-menu-host" data-cc-host></div>'
        + '</div>' + (phoneErr ? '<span class="nob-err" id="nob-err-phone" role="alert">Skriv inn et gyldig telefonnummer</span>' : '') + '</label>';

      body += '<label class="nob-field"><span class="nob-label">E-post</span>'
        + '<input class="nob-input' + (emailErr ? ' is-err' : '') + '" data-f="email" value="' + esc(st.email) + '" inputmode="email" autocomplete="email" placeholder="navn@epost.no"' + (emailErr ? ' aria-invalid="true" aria-describedby="nob-err-email"' : '') + '>'
        + (emailErr ? '<span class="nob-err" id="nob-err-email" role="alert">Skriv inn en gyldig e-postadresse</span>' : '') + '</label>';

      if (st.submitErr) body += '<div class="nob-banner-err">' + esc(st.submitErr) + '</div>';
      var subLbl = st.submitting
        ? esc(copy.details.submitting)
        : (esc(copy.details.submit) + ' · ' + esc(fmtSlotShort()) + ' →');
      body += '<div class="nob-cta-wrap"><button type="button" class="nob-cta nob-cta--dated" data-submit' + (st.submitting ? ' disabled' : '') + '>' + subLbl + '</button>' + trustRowHTML() + '</div>';
      body += '</div>';
      return { html: body, wireBar: bar.wire };
    }

    function ccMenuHTML() {
      var q = st.ccQuery.trim().toLowerCase().replace(/^\+/, '');
      var items = [];
      function row(c) {
        return '<button type="button" role="option" aria-selected="' + (c.code === st.ccCode ? 'true' : 'false') + '" class="nob-cc-item' + (c.code === st.ccCode ? ' is-sel' : '') + '" data-cc="' + c.code + '" aria-label="' + esc(c.name + ' ' + c.dial) + '">'
          + '<span class="nob-cc-flag" aria-hidden="true">' + c.flag + '</span><span class="nob-cc-name">' + esc(c.name) + '</span><span class="nob-cc-dial">' + c.dial + '</span></button>';
      }
      if (q) {
        COUNTRIES.filter(function (c) { return c.name.toLowerCase().indexOf(q) > -1 || c.dial.replace('+', '').indexOf(q) > -1; }).forEach(function (c) { items.push(row(c)); });
      } else {
        items.push('<div class="nob-cc-hdr">Norden</div>');
        COUNTRIES.filter(function (c) { return c.nordic; }).forEach(function (c) { items.push(row(c)); });
        items.push('<div class="nob-cc-hdr">Alle land</div>');
        COUNTRIES.filter(function (c) { return !c.nordic; }).forEach(function (c) { items.push(row(c)); });
      }
      var inner = items.length ? items.join('') : '<div class="nob-cc-empty">Fant ikke landet</div>';
      return '<div class="nob-cc-backdrop" data-cc-close></div><div class="nob-cc-menu">'
        + '<div class="nob-cc-search"><input data-cc-q value="' + esc(st.ccQuery) + '" placeholder="Søk land eller kode…" aria-label="Søk etter land eller landskode"></div>'
        + '<div class="nob-cc-list" role="listbox" aria-label="Velg land">' + inner + '</div></div>';
    }

    function confirmHTML() {
      var first = st.firstName.trim();
      var thanks = first ? esc(copy.confirm.thanksNamed).split('{first}').join(esc(first)) : esc(copy.confirm.thanksAnon);
      var emailHref = 'mailto:' + cfg.email;
      var body = '<div class="nob-screen nob-confirm">';
      body += '<div class="nob-confirm-top"><div class="nob-check">' + ICO.checkBig + '</div><div><h1>' + esc(copy.confirm.h1) + '</h1><p>' + thanks + '</p></div></div>';
      body += '<div class="nob-card">';
      body += '<div class="nob-card-row"><span class="nob-summary-k">' + esc(copy.confirm.kSlot) + '</span><span class="nob-card-big">' + esc(selSummary()) + '</span></div>';
      body += '<div class="nob-card-row"><span class="nob-summary-k">' + esc(copy.confirm.kLocation) + '</span><span class="nob-card-med">' + esc(cfg.location) + '</span>'
        + (cfg.mapsUrl ? '<a class="nob-maps" href="' + esc(cfg.mapsUrl) + '" target="_blank" rel="noopener">Vis vei →</a>' : '')
        + '</div>';
      if (st.ref) body += '<div class="nob-card-row"><span class="nob-summary-k">' + esc(copy.confirm.kRef) + '</span><span class="nob-card-med">' + esc(st.ref) + '</span></div>';
      body += '<div class="nob-card-row nob-card-status"><span class="nob-status-dot"></span><span>' + esc(copy.confirm.status) + '</span></div>';
      body += '</div>';
      body += '<div class="nob-promise"><p>' + esc(copy.confirm.reassure) + '</p></div>';
      // Nono-dugnaden: the «Hvem tipset deg?»-question lives HERE, after the
      // conversion is complete — it can never cost a booking. Skipped when the
      // session already carried a ?ref= code (we already know the referrer).
      if (cfg.vervHint && !VERV.code()) {
        body += '<div class="nob-vervhint" data-vervhint>'
          + '<span class="nob-vervhint-h">' + esc(copy.confirm.vervH) + '</span>'
          + '<span class="nob-vervhint-sub">' + esc(copy.confirm.vervSub) + '</span>'
          + '<div class="nob-vervhint-row">'
          + '<input type="text" maxlength="120" placeholder="' + esc(copy.confirm.vervPlaceholder) + '" data-vervhint-name>'
          + '<button type="button" class="nob-ghost" data-vervhint-send>' + esc(copy.confirm.vervSend) + '</button>'
          + '</div></div>';
      }
      body += '<div class="nob-intake-cta"><span class="nob-intake-h">' + esc(copy.confirm.intakeH) + '</span><span class="nob-intake-sub">' + esc(copy.confirm.intakeSub) + '</span><button type="button" class="nob-cta" data-go-intake>' + esc(copy.confirm.intakeCta) + '</button></div>';
      body += '<div class="nob-contact"><span>' + esc(copy.confirm.contactPrompt) + '</span><div class="nob-contact-row">'
        + '<a class="nob-contact-btn" href="tel:' + esc(cfg.phone) + '">' + ICO.phone + esc(cfg.phoneText) + '</a>'
        + '<a class="nob-contact-btn" href="' + esc(emailHref) + '">' + ICO.mail + esc(cfg.email) + '</a></div></div>';
      body += '<button type="button" class="nob-ghost nob-wide" data-restart>' + esc(copy.confirm.restart) + '</button>';
      body += '</div>';
      return { html: body };
    }

    function optList(opts, selected, multi, dataKey) {
      return opts.map(function (label) {
        var sel = multi ? selected.indexOf(label) > -1 : selected === label;
        return '<button type="button" class="nob-opt' + (sel ? ' is-sel' : '') + '" data-' + dataKey + '="' + esc(label) + '">'
          + '<span>' + esc(label) + '</span><span class="nob-opt-ind' + (multi ? ' is-sq' : '') + (sel ? ' is-on' : '') + '">' + (sel ? '✓' : '') + '</span></button>';
      }).join('');
    }

    function intakeHTML() {
      var body = '<div class="nob-screen">';
      body += '<div class="nob-head"><button type="button" class="nob-back" aria-label="Tilbake">' + ICO.back + '</button><span class="nob-booked-pill">' + ICO.check + ' ' + esc(copy.intake.bookedPill) + '</span></div>';
      body += '<div class="nob-title"><h2>' + esc(copy.intake.titleH) + '</h2><p>' + esc(copy.intake.titleSub) + '</p></div>';

      body += '<div class="nob-q"><span class="nob-q-h">' + esc(copy.intake.qAge) + ' <span class="nob-opt-x">' + esc(copy.intake.optionalTag) + '</span></span><div class="nob-opts">' + optList(copy.intake.alder, st.iAlder, false, 'alder') + '</div></div>';

      body += '<div class="nob-q"><span class="nob-q-h">' + esc(copy.intake.qAilments) + ' <span class="nob-opt-x">' + esc(copy.intake.optionalTag) + '</span></span>'
        + '<textarea class="nob-textarea" data-f="plager" rows="4" placeholder="' + esc(copy.intake.ailmentsPlaceholder) + '">' + esc(st.iPlager) + '</textarea></div>';

      body += '<div class="nob-q"><span class="nob-q-h">' + esc(copy.intake.qExperience) + '</span><div class="nob-opts">' + optList(copy.intake.erfaring, st.iErfaring, false, 'erf') + '</div>'
        + (st.iTried && !st.iErfaring ? '<span class="nob-err">Velg ett alternativ</span>' : '') + '</div>';

      body += '<div class="nob-q"><span class="nob-q-h">' + esc(copy.intake.qReason) + ' <span class="nob-opt-x">' + esc(copy.intake.manyTag) + '</span></span><div class="nob-opts">' + optList(copy.intake.grunner, st.iGrunner, true, 'grunn') + '</div>'
        + (st.iTried && st.iGrunner.length === 0 ? '<span class="nob-err">Velg minst ett alternativ</span>' : '') + '</div>';

      body += '<div class="nob-cta-wrap"><button type="button" class="nob-cta" data-submit-intake>' + esc(copy.intake.submit) + '</button><button type="button" class="nob-link" data-skip-intake>' + esc(copy.intake.skip) + '</button></div>';
      body += '</div>';
      return { html: body };
    }

    function intakeDoneHTML() {
      return '<div class="nob-screen nob-confirm"><div class="nob-confirm-top"><div class="nob-check">' + ICO.checkBig + '</div><div><h1>' + esc(copy.done.h1) + '</h1>'
        + '<p>' + esc(copy.done.body).split('[sted]').join(esc(cfg.location)) + '</p></div></div>'
        + '<button type="button" class="nob-ghost nob-wide" data-restart>' + esc(copy.done.restart) + '</button></div>';
    }

    // ---- render + wire -----------------------------------------------------
    function render(animate) {
      var built;
      if (st.step === 'hero') built = { html: heroHTML() };
      else if (st.step === 'choose') built = chooserHTML();
      else if (st.step === 'time') built = timeHTML();
      else if (st.step === 'details') built = detailsHTML();
      else if (st.step === 'confirm') built = confirmHTML();
      else if (st.step === 'intake') built = intakeHTML();
      else if (st.step === 'intakedone') built = { html: intakeDoneHTML() };
      else built = { html: heroHTML() };

      var firstRender = root.dataset.firstRenderDone !== '1';
      root.innerHTML = built.html;
      // Only run the entry animation when ARRIVING at a step — never on in-step
      // re-renders (day/slot taps, validation), which would flash the panel.
      // Honour prefers-reduced-motion (skip the slide for sensitive users).
      var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion:reduce)').matches;
      if (animate) {
        var sc = root.querySelector('.nob-screen');
        if (sc && !reduceMotion) sc.style.animation = 'nobIn .35s ease both';
        // Move focus to the step heading so screen-reader/keyboard users follow
        // the change — but NOT on the very first paint (would steal focus / scroll).
        if (!firstRender) {
          var h = root.querySelector('h1, h2');
          if (h) { h.setAttribute('tabindex', '-1'); try { h.focus({ preventScroll: true }); } catch (e) { try { h.focus(); } catch (e2) {} } }
        }
      }
      root.dataset.firstRenderDone = '1';
      if (built.wireBar) built.wireBar(root);
      wire();
    }

    function bindInputs() {
      root.querySelectorAll('[data-f]').forEach(function (el) {
        var f = el.getAttribute('data-f');
        el.addEventListener('input', function () {
          if (f === 'firstName') st.firstName = el.value;
          else if (f === 'lastName') st.lastName = el.value;
          else if (f === 'phone') st.phone = el.value;
          else if (f === 'email') st.email = el.value;
          else if (f === 'plager') st.iPlager = el.value;
        });
      });
    }

    function renderCcMenu() {
      var hostEl = root.querySelector('[data-cc-host]');
      if (!hostEl) return;
      hostEl.innerHTML = st.ccOpen ? ccMenuHTML() : '';
      if (!st.ccOpen) return;
      function closeCc() {
        st.ccOpen = false; renderCcMenu();
        var t = root.querySelector('[data-cc-toggle]');
        if (t) { t.setAttribute('aria-expanded', 'false'); try { t.focus(); } catch (e) {} }
      }
      var close = hostEl.querySelector('[data-cc-close]');
      if (close) close.addEventListener('click', closeCc);
      var q = hostEl.querySelector('[data-cc-q]');
      if (q) { q.focus(); q.addEventListener('input', function () { st.ccQuery = q.value; renderCcMenu(); }); }
      // Escape closes ONLY the country menu — stop it bubbling to the modal's
      // document-level Escape (which would close the whole booking dialog).
      hostEl.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' || e.keyCode === 27) { e.stopPropagation(); e.preventDefault(); closeCc(); }
      });
      hostEl.querySelectorAll('[data-cc]').forEach(function (b) {
        b.addEventListener('click', function () {
          st.ccCode = b.getAttribute('data-cc'); st.ccOpen = false; st.ccQuery = '';
          // re-render the cc button label + aria without dropping the rest of the form
          var btn = root.querySelector('[data-cc-toggle]');
          var c = country();
          if (btn) {
            btn.innerHTML = '<span class="nob-cc-flag" aria-hidden="true">' + c.flag + '</span><span>' + c.dial + '</span><span class="nob-cc-caret" aria-hidden="true">▾</span>';
            btn.setAttribute('aria-expanded', 'false');
            btn.setAttribute('aria-label', 'Landskode, valgt ' + c.name);
            try { btn.focus(); } catch (e) {}
          }
          renderCcMenu();
        });
      });
    }

    function wire() {
      // hero / nav — med valgsteget på går hero-CTA-en til de to dørene i
      // stedet for rett til kalenderen (availability preloades uansett).
      root.querySelectorAll('[data-go-time]').forEach(function (b) { b.addEventListener('click', function () { if (!inModal) TRK.send('flow_start', { stepName: 'hero' }); if (!st.days) loadAvailability(); st.submitErr = ''; if (chooserOn()) { go('choose'); trackChooserView(); return; } go('time'); if (st.days && st.days.length) trackCalendarView(); }); });

      // valgsteget (to dører)
      var chSelf = root.querySelector('[data-choose-self]');
      if (chSelf) chSelf.addEventListener('click', function () { if (!st.days) loadAvailability(); go('time'); if (st.days && st.days.length) trackCalendarView(); });
      var chCall = root.querySelector('[data-choose-call]');
      if (chCall) chCall.addEventListener('click', function () { st.cmOpen = true; render(); });
      var chBack = root.querySelector('[data-choose-back]');
      if (chBack) chBack.addEventListener('click', function () { go('hero'); });

      // «ring meg»-flatene (valgsteget + kalender-fallbacken)
      var cmSub = root.querySelector('[data-callme-submit]');
      if (cmSub) cmSub.addEventListener('click', function () { submitCallme(st.step === 'choose' ? 'choose' : 'fallback'); });
      var cmDis = root.querySelector('[data-callme-dismiss]');
      if (cmDis) cmDis.addEventListener('click', function () {
        st.fbShown = false; st.fbDismissed = true; render();
        var h2 = root.querySelector('h2');
        if (h2) { h2.setAttribute('tabindex', '-1'); try { h2.focus({ preventScroll: true }); } catch (e) {} }
      });
      var cmOpen = root.querySelector('[data-callme-open]');
      if (cmOpen) cmOpen.addEventListener('click', function () {
        st.fbShown = true; TRK.send('callback_offer', { stepName: 'time' }); render();
        var fIn = root.querySelector('.nob-fbcard [data-f="firstName"]');
        if (fIn) { try { fIn.focus({ preventScroll: true }); } catch (e) {} }
      });

      // time step
      var retry = root.querySelector('[data-retry]'); if (retry) retry.addEventListener('click', loadAvailability);
      root.querySelectorAll('[data-day]').forEach(function (b) {
        b.addEventListener('click', function () {
          var v = b.getAttribute('data-day');
          // WHICH day, and how deep in the strip it sits. Without these a
          // day_select is just "they tapped something" — and the question that
          // decides whether we open evenings/Saturdays (are they hunting for a
          // weekend? for a date far ahead? or just toggling inside this week?)
          // cannot be answered at all.
          var dIdx = null;
          try {
            for (var di = 0; di < (st.days || []).length; di++) {
              if (st.days[di].date === v) { dIdx = di; break; }
            }
          } catch (e) {}
          TRK.send('day_select', { stepName: 'time', dayIso: v, dayIndex: dIdx });
          // Jeger-signalet: mange dagbytter uten valgt tid = personen leter og
          // finner ikke. Etter N bytter glir «ring meg»-kortet inn av seg selv.
          if (v !== st.selDay) st.dayTaps++;
          if (fallbackOn() && !st.fbShown && !st.fbDismissed && !st.cmDone && !st.selSlot && st.dayTaps >= (Number(CM.fallbackAfterTaps) || 4)) {
            st.fbShown = true;
            TRK.send('callback_offer', { stepName: 'time' });
          }
          st.selDay = v; st.selSlot = null; render();
          // preventScroll so the browser's focus doesn't yank the day to the edge —
          // smartScrollStrip() (run inside render) decides the strip position.
          var nb = root.querySelector('[data-day="' + v + '"]'); if (nb) { try { nb.focus({ preventScroll: true }); } catch (e) { try { nb.focus(); } catch (e2) {} } }
        });
      });
      root.querySelectorAll('[data-slot]').forEach(function (b) {
        b.addEventListener('click', function () {
          var v = b.getAttribute('data-slot');
          TRK.send('slot_select', { stepName: 'time', slotIso: v });
          st.selSlot = v; render();
          // The sticky CTA footer keeps "Fortsett" visible while slots scroll, so
          // no scroll-into-view is needed on select — just restore focus.
          var nb = root.querySelector('[data-slot="' + v + '"]'); if (nb) { try { nb.focus(); } catch (e) {} }
        });
      });
      root.querySelectorAll('[data-scroll]').forEach(function (b) {
        b.addEventListener('click', function () {
          var strip = root.querySelector('[data-strip]'); if (!strip) return;
          var dir = +b.getAttribute('data-scroll');
          strip.scrollBy({ left: dir * Math.round(strip.clientWidth * 0.7), behavior: 'smooth' });
        });
      });
      var goDet = root.querySelector('[data-go-details]'); if (goDet) goDet.addEventListener('click', function () { if (st.selSlot) { TRK.send('details_open', { stepName: 'details' }); go('details'); } });
      setupStripDrag();
      smartScrollStrip();

      // details step
      bindInputs();
      var edit = root.querySelector('[data-edit]'); if (edit) edit.addEventListener('click', function () { go('time'); });
      var ccToggle = root.querySelector('[data-cc-toggle]'); if (ccToggle) ccToggle.addEventListener('click', function () { st.ccOpen = !st.ccOpen; st.ccQuery = ''; renderCcMenu(); ccToggle.setAttribute('aria-expanded', st.ccOpen ? 'true' : 'false'); });
      var submit = root.querySelector('[data-submit]'); if (submit) submit.addEventListener('click', submitBooking);

      // confirm step
      var goIntake = root.querySelector('[data-go-intake]'); if (goIntake) goIntake.addEventListener('click', function () { TRK.send('intake_open', { stepName: 'intake' }); go('intake'); });

      // Nono-dugnaden: send the tip-name (fire-and-forget, post-conversion).
      var vervSend = root.querySelector('[data-vervhint-send]');
      if (vervSend) vervSend.addEventListener('click', function () {
        var box = root.querySelector('[data-vervhint]');
        var inp = root.querySelector('[data-vervhint-name]');
        var name = inp && inp.value ? inp.value.trim() : '';
        if (!name || name.length < 2 || !st.ref) return;
        try {
          fetch(endpoint + '/api/referral-hint', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ bookingRef: st.ref, tipperName: name.slice(0, 120) }),
            keepalive: true
          }).catch(function () {});
        } catch (e) {}
        if (box) box.innerHTML = '<span class="nob-vervhint-h">' + esc(copy.confirm.vervThanks) + '</span>';
      });

      // intake step
      root.querySelectorAll('[data-alder]').forEach(function (b) { b.addEventListener('click', function () { st.iAlder = b.getAttribute('data-alder'); render(); }); });
      root.querySelectorAll('[data-erf]').forEach(function (b) { b.addEventListener('click', function () { st.iErfaring = b.getAttribute('data-erf'); render(); }); });
      root.querySelectorAll('[data-grunn]').forEach(function (b) {
        b.addEventListener('click', function () {
          var l = b.getAttribute('data-grunn');
          var i = st.iGrunner.indexOf(l);
          if (i > -1) st.iGrunner.splice(i, 1); else st.iGrunner.push(l);
          render();
        });
      });
      var subInt = root.querySelector('[data-submit-intake]'); if (subInt) subInt.addEventListener('click', submitIntake);
      var skip = root.querySelector('[data-skip-intake]'); if (skip) skip.addEventListener('click', function () { TRK.send('intake_skip', { stepName: 'intake' }); restart(); });

      // back button on intake step
      var headBack = root.querySelector('.nob-head .nob-back');
      if (headBack && st.step === 'intake') { headBack.onclick = function () { go('confirm'); }; }

      // restart
      root.querySelectorAll('[data-restart]').forEach(function (b) { b.addEventListener('click', restart); });
    }

    function setupStripDrag() {
      var strip = root.querySelector('[data-strip]'); if (!strip) return;
      var down = false, startX = 0, startScroll = 0, moved = false;
      strip.addEventListener('pointerdown', function (e) { down = true; moved = false; startX = e.clientX; startScroll = strip.scrollLeft; });
      strip.addEventListener('pointermove', function (e) { if (!down) return; var dx = e.clientX - startX; if (Math.abs(dx) > 5) moved = true; strip.scrollLeft = startScroll - dx; });
      function up() { down = false; }
      strip.addEventListener('pointerup', up);
      strip.addEventListener('pointerleave', up);
      // suppress click after a drag
      strip.addEventListener('click', function (e) { if (moved) { e.stopPropagation(); e.preventDefault(); moved = false; } }, true);
      // Remember the strip scroll across re-renders (manual drag / Senere / Tidligere)
      // so a same-day re-render (e.g. a slot tap) doesn't snap it back to the start.
      strip.addEventListener('scroll', function () { root._stripScroll = strip.scrollLeft; });
    }

    // Position the day-strip so the selected day keeps TWO days visible to its
    // LEFT (which also reveals the upcoming days on the right). render() rebuilds
    // innerHTML so the strip resets to scroll 0 each time; we set the position
    // INSTANTLY here — before the browser paints — so there is NO scroll
    // animation. Clamped, so early days simply sit at the start with no movement.
    // A same-day re-render (slot tap / validation) just restores the remembered
    // scroll so it never jumps.
    function smartScrollStrip() {
      var strip = root.querySelector('[data-strip]');
      if (!strip) return;
      if (st.selDay === root._stripSelDay) { strip.scrollLeft = root._stripScroll || 0; return; }
      root._stripSelDay = st.selDay;
      var sel = strip.querySelector('.nob-day.is-sel');
      if (!sel) { root._stripScroll = 0; return; }
      var first = strip.children[0], second = strip.children[1];
      var stepPx = (first && second) ? (second.offsetLeft - first.offsetLeft) : (sel.offsetWidth + 10);
      // Two day-cards of lead-in to the left of the selected day.
      var max = Math.max(0, strip.scrollWidth - strip.clientWidth);
      var target = Math.max(0, Math.min(sel.offsetLeft - stepPx * 2, max));
      root._stripScroll = target;
      strip.scrollLeft = target; // instant — no animation
    }

    // Reset to a fresh flow + reload availability — called when a modal opens.
    function openReset() {
      st.selSlot = null; st.firstName = ''; st.lastName = ''; st.phone = ''; st.email = '';
      st.ccCode = 'NO'; st.ccOpen = false; st.ccQuery = '';
      applyPrefill();
      st.tried = false; st.ref = null; st.submitErr = '';
      st.iAlder = ''; st.iPlager = ''; st.iErfaring = ''; st.iGrunner = []; st.iTried = false;
      st.step = baseStart();
      st.calSeen = false;
      cmReset();
      loadAvailability();
      render(true);
      if (st.step === 'choose') trackChooserView();
    }
    root.__nob = { reset: openReset, step: function () { return st.step; } };

    // ---- boot --------------------------------------------------------------
    if (st.step !== 'hero') loadAvailability();
    render(true);
  }

  // Modal controller: any [data-booking-trigger] (or header booking links) on the
  // page opens the booking flow in a dialog. Reuses the same triggers the old
  // Momence-iframe modal used, so no copy section needs editing.
  function setupModal(modalEl) {
    if (modalEl.dataset.bound === '1') return;
    modalEl.dataset.bound = '1';
    var root = modalEl.querySelector('[data-nono-booking]');
    var lastFocus = null;
    var lockedScrollY = 0;
    // Robust scroll lock: overflow:hidden alone does NOT stop the page behind
    // from scrolling on iOS Safari (rubber-band) or when scroll chains past the
    // panel's edges. Pin <body> with position:fixed at the current offset, then
    // restore on close. The panel itself has overscroll-behavior:contain (CSS),
    // so in-panel scrolling never leaks to the document.
    function lockScroll() {
      if (document.documentElement.getAttribute('data-nob-scrolllock') === '1') return;
      lockedScrollY = window.scrollY || window.pageYOffset || 0;
      var b = document.body;
      b.style.position = 'fixed';
      b.style.top = -lockedScrollY + 'px';
      b.style.left = '0';
      b.style.right = '0';
      b.style.width = '100%';
      b.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.setAttribute('data-nob-scrolllock', '1');
    }
    function unlockScroll() {
      if (document.documentElement.getAttribute('data-nob-scrolllock') !== '1') return;
      var b = document.body;
      b.style.position = '';
      b.style.top = '';
      b.style.left = '';
      b.style.right = '';
      b.style.width = '';
      b.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.documentElement.removeAttribute('data-nob-scrolllock');
      try { window.scrollTo(0, lockedScrollY); } catch (e) {}
    }
    function open() {
      lastFocus = document.activeElement;
      modalEl.hidden = false;
      TRK.send('modal_open');
      lockScroll();
      if (root && root.__nob) root.__nob.reset();
      var closeBtn = modalEl.querySelector('[data-nob-close]');
      if (closeBtn) { try { closeBtn.focus(); } catch (e) {} }
    }
    function close() {
      try { TRK.send('closed', { stepName: (root && root.__nob && root.__nob.step) ? root.__nob.step() : null }); } catch (e) {}
      modalEl.hidden = true;
      unlockScroll();
      if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
    }
    var TRIG = '[data-booking-trigger], a[href="#lp-booking"], a[href="#memberCreateForm"]';
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target.closest(TRIG) : null;
      if (!t) return;
      e.preventDefault();
      open();
    });
    modalEl.querySelectorAll('[data-nob-close]').forEach(function (b) { b.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) { if ((e.key === 'Escape' || e.keyCode === 27) && !modalEl.hidden) close(); });
    // Focus trap: keep Tab/Shift+Tab inside the dialog (computed live because
    // the widget re-renders its innerHTML between steps).
    modalEl.addEventListener('keydown', function (e) {
      if (modalEl.hidden || (e.key !== 'Tab' && e.keyCode !== 9)) return;
      var nodes = modalEl.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
      var list = [];
      for (var i = 0; i < nodes.length; i++) { if (nodes[i].offsetParent !== null) list.push(nodes[i]); }
      if (!list.length) return;
      var first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); try { last.focus(); } catch (er) {} }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); try { first.focus(); } catch (er) {} }
    });
  }

  function boot() {
    try { adAttr(); } catch (e) {} // persist the ad-landing (URL + fbclid) on first paint
    document.querySelectorAll('[data-nono-booking]').forEach(init);
    document.querySelectorAll('.nob-modal').forEach(setupModal);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
